// components/models/GptImage25ControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import type { GptImage25Mode, GptImage25Params, GptImage25Quality } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { GPT_IMAGE_25_DEFAULT_PARAMS } from '#lib/gpt-image-25';

export const getDefaultGptImage25Params = (): GptImage25Params => ({ ...GPT_IMAGE_25_DEFAULT_PARAMS });

interface GptImage25ControlPanelProps {
  params: GptImage25Params;
  setModelParams: (updater: (previous: { [model: string]: GptImage25Params }) => { [model: string]: GptImage25Params }) => void;
  diffusionModel: string;
}

const GptImage25ControlPanel: React.FC<GptImage25ControlPanelProps> = ({ params, setModelParams, diffusionModel }) => {
  const { kernel } = useKernel();
  const updateParam = useCallback((name: keyof GptImage25Params, value: string) => {
    setModelParams(previous => {
      const current = previous[diffusionModel];
      if (!current) return previous;
      return {
        ...previous,
        [diffusionModel]: { ...current, [name]: value },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: name === 'prompt' ? undefined : value,
      value_length: name === 'prompt' ? value.length : undefined,
      model_name: diffusionModel,
      source_component: 'GptImage25ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the image to create or edit..."
          name="prompt"
          value={params.prompt}
          onChange={event => updateParam('prompt', event.target.value)}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>
      <div className="mt-4">
        <Select
          id="mode"
          label="Mode"
          value={params.mode}
          options={['sunburst', 'flare']}
          onChange={value => updateParam('mode', value as GptImage25Mode)}
          style={{ width: '100%' }}
        />
      </div>
      <div className="mt-4">
        <Select
          id="quality"
          label="Quality"
          value={params.quality}
          options={['low', 'medium', 'high', 'xhigh', 'max']}
          onChange={value => updateParam('quality', value as GptImage25Quality)}
          style={{ width: '100%' }}
        />
      </div>
    </>
  );
};

export default memo(GptImage25ControlPanel);
